import type { ClientFlowAction } from '~/graphql/types.client';

/**
 * Given the currently selected (copied) actions and all flow actions,
 * returns a Set of action IDs that are immediate neighbors of the
 * selection and can be added to it. A neighbor is a direct parent or
 * direct child of any selected action.
 *
 * Start actions are excluded since they cannot be copied.
 */
const getSelectableNeighborIds = ({
  selectedActions,
  allActions,
}: {
  selectedActions: Array<ClientFlowAction>;
  allActions: Array<ClientFlowAction>;
}): Set<string> => {
  const selectedIds = new Set(selectedActions.map(a => a.id));
  const neighborIds = new Set<string>();

  const actionMap = new Map<string, ClientFlowAction>();
  for (const action of allActions) {
    actionMap.set(action.id, action);
  }

  for (const action of selectedActions) {
    // Add the direct (primary) parent — the first entry in parentIds
    if ('parentIds' in action && action.parentIds.length > 0) {
      const primaryParentId = action.parentIds[0];
      if (!selectedIds.has(primaryParentId)) {
        const parent = actionMap.get(primaryParentId);
        if (parent && parent.__typename !== 'FlowV2_Action_Start') {
          neighborIds.add(primaryParentId);
        }
      }
    }

    // Add direct children whose primary parent is this action
    for (const candidate of allActions) {
      if (
        candidate.__typename !== 'FlowV2_Action_Start' &&
        !selectedIds.has(candidate.id) &&
        'parentIds' in candidate &&
        candidate.parentIds[0] === action.id
      ) {
        neighborIds.add(candidate.id);
      }
    }

    // For IfElse actions, also add trueChild/falseChild explicitly
    if (action.__typename === 'FlowV2_Action_IfElse') {
      for (const childId of [action.trueChildId, action.falseChildId]) {
        if (childId && !selectedIds.has(childId)) {
          const child = actionMap.get(childId);
          if (child && child.__typename !== 'FlowV2_Action_Start') {
            neighborIds.add(childId);
          }
        }
      }
    }
  }

  return neighborIds;
};

export default getSelectableNeighborIds;
